import { paymentTypeSchema } from '@server/schemas'
import { z } from 'zod'

import { SUBSCRIBE_TICKETS_TAB, SubscribesTicketPaymentType } from '../model/subscribes-tickets-model'

type PaymentTypePriorityConfig = {
  subscribeTicketsEnabled: boolean
  /**
   * payback_tickets flag의 enabled + hideAt 통합 판정 결과.
   * 기본 우선순위의 PAYBACK 탭 노출 여부와 enable=PAYBACK URL 접근 허용 여부를 동시에 제어한다.
   */
  paybackAccessible: boolean
  accessEnableParam?: z.infer<typeof paymentTypeSchema> | 'PAYBACK'
  isSubscribeUrlAccessEnabled?: boolean
}

/**
 * 피처 플래그와 URL 파라미터에 따라 결제 타입의 우선순위를 반환합니다.
 * @description
 * - enable=SUBSCRIBE + URL 접근 허용 시: [SUBSCRIBE]
 * - enable=PAYBACK + payback 접근 허용 시: [PAYBACK]
 * - subscribeTicketsEnabled가 true인 경우: SUBSCRIBE -> LUMP_SUM -> PAYBACK -> IPAD
 * - subscribeTicketsEnabled가 false인 경우: LUMP_SUM -> PAYBACK -> IPAD
 */
export const getPaymentTypePriority = ({
  subscribeTicketsEnabled,
  paybackAccessible,
  accessEnableParam,
  isSubscribeUrlAccessEnabled,
}: PaymentTypePriorityConfig): SubscribesTicketPaymentType[] => {
  if (accessEnableParam === SUBSCRIBE_TICKETS_TAB.SUBSCRIBE && isSubscribeUrlAccessEnabled) {
    return [SUBSCRIBE_TICKETS_TAB.SUBSCRIBE]
  }

  if (accessEnableParam === SUBSCRIBE_TICKETS_TAB.PAYBACK && paybackAccessible) {
    return [SUBSCRIBE_TICKETS_TAB.PAYBACK]
  }

  const defaultPriority: SubscribesTicketPaymentType[] = [
    SUBSCRIBE_TICKETS_TAB.SUBSCRIBE,
    SUBSCRIBE_TICKETS_TAB.LUMP_SUM,
    SUBSCRIBE_TICKETS_TAB.PAYBACK,
    SUBSCRIBE_TICKETS_TAB.IPAD,
  ]

  return defaultPriority.filter((type) => {
    if (type === SUBSCRIBE_TICKETS_TAB.SUBSCRIBE) {
      return subscribeTicketsEnabled
    }

    if (type === SUBSCRIBE_TICKETS_TAB.PAYBACK) {
      return paybackAccessible
    }

    return true
  })
}

type FinalPaymentTypeConfig = {
  paymentTypeParam?: string
  isSubscribeEnabledByUrl: boolean
  subscribeTicketsEnabled: boolean
  isPaybackEnabledByUrl: boolean
}

/**
 * @description
 * 우선순위:
 * 1. enable=SUBSCRIBE → 강제로 SUBSCRIBE
 * 2. enable=PAYBACK → 강제로 PAYBACK
 * 3. URL paymentType 파라미터
 * 4. 플래그 기반 기본값 (구독 활성화 시 SUBSCRIBE, 아니면 LUMP_SUM)
 */
export const getFinalPaymentType = ({
  paymentTypeParam,
  isSubscribeEnabledByUrl,
  subscribeTicketsEnabled,
  isPaybackEnabledByUrl,
}: FinalPaymentTypeConfig): SubscribesTicketPaymentType => {
  if (isSubscribeEnabledByUrl) {
    return SUBSCRIBE_TICKETS_TAB.SUBSCRIBE
  }

  if (isPaybackEnabledByUrl) {
    return SUBSCRIBE_TICKETS_TAB.PAYBACK
  }

  if (paymentTypeParam && paymentTypeParam !== SUBSCRIBE_TICKETS_TAB.BUSINESS) {
    return paymentTypeParam as SubscribesTicketPaymentType
  }

  return subscribeTicketsEnabled ? SUBSCRIBE_TICKETS_TAB.SUBSCRIBE : SUBSCRIBE_TICKETS_TAB.LUMP_SUM
}
